import { useEffect, useRef } from "react";

import { useDispatch, useSelector, useStore } from "react-redux";

import {
  deleteCompletedTask,
  pollTranscriptionStatus,
} from "@/redux/transcription-summary";
import { type AppDispatch, persistor } from "@/store";
import { clearNotification, showLoading, showSuccess } from "@/utils/notification";

// Define types for transcription tasks
interface TranscriptionTask {
  taskId: string;
  taskType: "voiceToText" | "assessmentScribe";
  status: string;
  startTime: number;
  endTime?: number;
  result?: string;
  caseNumber?: string;
  metadata?: {
    callback?: (result: string) => void;
    caseId?: string;
    parentTaskId?: string;
    notes?: string;
  };
}

interface TranscriptionState {
  activeTasks: Record<string, TranscriptionTask>;
  completedTasks: Record<string, TranscriptionTask>;
}

interface RootState {
  transcription: TranscriptionState;
  notification?: { message: { content: string; isLoading?: boolean } | null };
}

const isPending = (task: TranscriptionTask) =>
  task.status !== "SUCCESS" && task.status !== "FAILURE";

const transcribingMessage = (tasks: TranscriptionTask[]) => {
  const caseNumbers = Array.from(
    new Set(tasks.map((task) => task.caseNumber).filter(Boolean))
  );
  const caseInfo =
    caseNumbers.length > 0
      ? ` for Case${caseNumbers.length > 1 ? "s" : ""} ${caseNumbers.join(", ")}`
      : "";
  return `Transcribing assessment${caseInfo}`;
};

export const TranscriptionStatusNotifier = () => {
  const dispatch = useDispatch<AppDispatch>();
  const store = useStore<RootState>();

  const activeTasks = useSelector(
    (state: RootState) => state.transcription.activeTasks
  );
  const completedTasks = useSelector(
    (state: RootState) => state.transcription.completedTasks
  );

  const activeTaskCount = Object.keys(activeTasks).length;
  // Status updates re-run the effects: resume a task once, re-show the toast only on new text.
  const resumedTaskIds = useRef(new Set<string>());
  const lastLoadingMessage = useRef<string | null>(null);
  const recentCompletedTask = Object.values(completedTasks).sort(
    (a: TranscriptionTask, b: TranscriptionTask) =>
      (b.endTime || 0) - (a.endTime || 0)
  )[0];

  // Resume restored tasks once, after rehydration; this tab's own tasks already poll.
  useEffect(() => {
    const resume = () => {
      const { activeTasks: restored } = store.getState().transcription;
      // Poll for each pending task. Anchor the elapsed cap (MDRS-223) to the
      // task's creation time, not now — otherwise every remount/re-fire of
      // this effect would hand the poll a fresh 20-min budget and a hung task
      // could be polled forever while the tab stays open.
      Object.values(restored)
        .filter(
          (task) => isPending(task) && !resumedTaskIds.current.has(task.taskId)
        )
        .forEach((task) => {
          resumedTaskIds.current.add(task.taskId);
          dispatch(
            pollTranscriptionStatus({
              taskId: task.taskId,
              caseNumber: task.caseNumber || null,
              notes: task.metadata?.notes || null,
              startedAt: task.startTime,
              // Restored from an earlier page; its failure shows on the case, not as a toast.
              quiet: !!task.caseNumber,
            })
          );
        });
    };
    if (persistor.getState().bootstrapped) {
      resume();
      return;
    }
    const unsubscribe = persistor.subscribe(() => {
      if (!persistor.getState().bootstrapped) return;
      unsubscribe();
      resume();
    });
    return unsubscribe;
  }, [dispatch, store]);

  // Show success toast for recently completed task
  useEffect(() => {
    if (
      recentCompletedTask &&
      "endTime" in recentCompletedTask &&
      recentCompletedTask.endTime &&
      recentCompletedTask.endTime > Date.now() - 5000
    ) {
      const caseInfo =
        "caseNumber" in recentCompletedTask && recentCompletedTask.caseNumber
          ? ` for Case ${recentCompletedTask.caseNumber}`
          : "";

      if (
        "taskType" in recentCompletedTask &&
        recentCompletedTask.taskType === "voiceToText"
      ) {
        showSuccess(`Transcription complete${caseInfo}!`, dispatch);
      } else {
        showSuccess(`Transcription & Summarization complete${caseInfo}.`, dispatch);
      }
    }
  }, [recentCompletedTask, dispatch]);

  // Show loading toast while tasks are active
  useEffect(() => {
    if (activeTaskCount > 0) {
      const currentActiveTasks = Object.values(activeTasks);
      const newTasks = currentActiveTasks.filter(isPending);
      currentActiveTasks.forEach((task: TranscriptionTask) => {
        if (!isPending(task)) {
          dispatch(deleteCompletedTask({ taskId: task.taskId }));
        }
      });

      if (newTasks.length === 0) {
        return;
      }

      const message = transcribingMessage(newTasks);
      if (message !== lastLoadingMessage.current) {
        lastLoadingMessage.current = message;
        showLoading(message, dispatch);
      }
    } else {
      // A quietly ended task shows no toast of its own, so take down "Transcribing…".
      const shown = store.getState().notification?.message;
      if (shown?.isLoading && shown.content === lastLoadingMessage.current) {
        clearNotification(dispatch);
      }
      lastLoadingMessage.current = null;
    }
  }, [activeTaskCount, activeTasks, dispatch, store]);

  return null;
};
